import { isUserAuthenticated } from "@/middleware/auth";

import { SocketEventActionType, UserEventType } from "./types";
import { useAuthState } from "@/zustand/AuthState";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import { navigateToLogin } from "@/lib/navigationRef";
import { createLogger } from "@/utils/debug";

const log = createLogger("userEventsHandler");

/**
 * Last identity this handler saw with auth intact. The 401-refresh-failure
 * race wipes the auth store while the socket is still connected — this
 * snapshot lets the DELETE branch keep a POSITIVE identity match through
 * that window instead of degrading to "match anyone" (2026-08-17 review §3).
 */
let lastKnownUsername: string | null = null;

/**
 * Called by logoutUser (dynamic import — this module statically imports
 * auth): a deliberate logout ENDS the identity, and the snapshot must not
 * survive into a next account's session on this tab, where it could wrongly
 * veto that account's own delete event. Also used by tests to isolate cases.
 */
export const resetLastKnownUsername = (): void => {
  lastKnownUsername = null;
};

export const handleUserEvent = async (data: UserEventType) => {
  // Snapshot the identity on EVERY event while auth is intact — see
  // lastKnownUsername above.
  const authed = isUserAuthenticated();
  if (authed?.username) {
    lastKnownUsername = authed.username;
  }

  // The DELETE branch must run even when auth is already PARTIALLY wiped
  // (the 401 interceptor's refresh-failure path calls resetAuthState, after
  // which isUserAuthenticated() is null) — this event IS the cleanup signal,
  // so it cannot be gated on an intact session.
  if (data.action === SocketEventActionType.DELETE) {
    await onDeleteUser({ eventPayload: data });
    return;
  }

  if (!authed) {
    return;
  }

  switch (data.action) {
    case SocketEventActionType.CREATE:
      break;
    case SocketEventActionType.UPDATE:
      onUpdateUser({ eventPayload: data });
      break;

    default:
      break;
  }
};

/**
 * `user` Delete — the broker emits this ONLY into the deleted user's own
 * namespace: it is this device's signal that the account is gone (deleted
 * from another tab/device, or by support). Without it, a second session
 * keeps a live socket and watches its chats vanish one by one while
 * appearing healthy, until an API call finally 401s.
 *
 * Idempotent local teardown + redirect, gated on a POSITIVE identity match:
 * the same channel demonstrably carries OTHER users' events (onUpdateUser
 * below handles avatar changes "for ANY user"), so a permissive guard would
 * let a stray delete event wipe a bystander session. When the payload names
 * a user, it must match the auth store OR the lastKnownUsername snapshot
 * (which survives the 401-wipe race); when neither identity is known, the
 * event is unverifiable and we skip — auth is already wiped in that state,
 * so Protected has logged the user out regardless. A payload with NO
 * username falls back to trusting the own-namespace routing.
 */
const onDeleteUser = async ({
  eventPayload,
}: {
  eventPayload: UserEventType;
}) => {
  const currentUsername = useAuthState.getState().username;
  const knownUsername = currentUsername || lastKnownUsername;
  const deletedUsername = eventPayload.user?.username;

  if (deletedUsername) {
    if (!knownUsername) {
      log.warn(
        "Ignoring user-delete event — no local identity left to verify against",
        { deletedUsername },
      );
      return;
    }
    if (deletedUsername !== knownUsername) {
      log.warn("Ignoring user-delete event for a different user", {
        deletedUsername,
      });
      return;
    }
  }

  log.info("Own account deleted — tearing down this session");
  try {
    // The import itself must be guarded too: after a redeploy the hashed
    // chunk can 404 (or we're offline), and an unguarded rejection here
    // would skip the redirect below — leaving alive the exact zombie
    // session this handler exists to kill.
    const { accountDeletedLocalTeardown } = await import(
      "@/middleware/accountDeletion"
    );
    // Pass the payload's username: the 401-refresh-failure race can wipe the
    // auth store before this runs, and the per-user push-key cleanup would
    // otherwise be skipped.
    await accountDeletedLocalTeardown({
      username: deletedUsername || knownUsername || undefined,
    });
  } catch {
    // Best-effort — the redirect below must happen regardless.
  }
  navigateToLogin();
};

const onUpdateUser = ({ eventPayload }: { eventPayload: UserEventType }) => {
  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    return;
  }

  const userEventData = eventPayload.user;

  // Update the user avatar map for ANY user whose avatar changed.
  // We use priority=true because websocket USER_UPDATE events are real-time
  // authoritative data from the server. The grace period only needs to protect
  // against stale message-derived avatars (setUserAvatarBatch), not live events.
  if (userEventData.username && userEventData.profile_image) {
    useUserAvatarMapState
      .getState()
      .setUserAvatar(
        userEventData.username,
        userEventData.profile_image,
        new Date().toISOString(),
        true,
      );
  } else if (userEventData.username && !userEventData.profile_image) {
    useUserAvatarMapState
      .getState()
      .removeUserAvatar(userEventData.username, true);
  }
};
